import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import useAuthenticatedAxios from '../../../auth/AuthenticatedAxios';
import AnalysisLoadingScreen from '../../Helpers/AnalysisLoadingScreen';
import { getCountryFlag } from './generateCaseReport';
import { useShareReport } from './useShareReport';
import { useDisplayPrefs } from './useDisplayPrefs';
import WidgetAssessments from '../Assessments/WidgetAssessments';
import ResumeBuilder from './ResumeBuilder/ResumeBuilder';
import { InfoRequestComposer, InfoRequestsPanel } from './ResumeBuilder/InfoRequests';
import { useWidgetConfig, useCapabilities } from '../../../Context/CapabilityContext';

import {
  Container,
  Header,
  HeaderLeft,
  HeaderTitle,
  HeaderBadge,
  HeaderSubtitle,
  HeaderRight,
  StatusBadge,
  RefreshButton,
  SpinningIcon,
  LastUpdated,
  MainContent,
  EmptyState,
  AnalyzeButton,
  LoadingSpinner,
  Section,
  SectionHeader,
  SectionContent,
  TwoColumnGrid,
  ThreeColumnGrid,
  InfoCard,
  CardLabel,
  CardValue,
  ProfileGrid,
  ProfileField,
  ProfileLabel,
  ProfileValue,
  QuickStatCard,
  BlockerList,
  RiskList,
  PathwaysGrid,
  PathwayCard,
  PathwayHeader,
  PathwayTitle,
  ScoreCircle,
  PathwaySummary,
  ConfidenceBadge,
  ProsCons,
  BreakdownGrid,
  BreakdownItem,
  ActionItemsList,
  ActionItem,
  MissingItemsGrid,
  MissingItem,
  ResolutionCheckbox,
  NextStepsList,
  NextStep,
  NoteItem,
  ComplianceGrid,
  ComplianceCard,
  ComplianceHeader,
  ComplianceSectionName,
  ComplianceStatusBadge,
  ComplianceDetail,
  ComplianceIssuesList,
  ComplianceSummaryBar,
  ComplianceStat,
  CommCard,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  CloseButton,
  ModalBody,
  SectionSelector,
  CheckboxGrid,
  CheckboxItem,
  ShareOptionsGrid,
  ShareOptionButton,
  SelectAllRow,
  ShareButton,
  CopiedToast,
  SettingsButton,
  ToggleSwitch,
  SettingsRow,
  SettingsCategory,
} from './CaseDashboardStyles';

/**
 * Sections that are gated by a display preference but had no switch in the
 * Display Settings modal. Order matches the order they appear in the report.
 *
 * Keep this in sync with getDefaultDisplayPrefs() in useDisplayPrefs.js — a key
 * here with no default, or a default with no row here, means an adviser sees a
 * section they cannot turn off.
 */
/**
 * Placeholder strings the analysis model writes when it has no data. These must
 * not count as "collected" — see filledProfileFields.
 */
const PROFILE_PLACEHOLDERS = new Set([
  'unknown', 'not provided', 'not specified', 'not stated', 'not established',
  'not available', 'not disclosed', 'not found', 'not applicable', 'not reported',
  'n/a', 'na', 'null', 'undefined', '-', '—', 'tbd', 'pending',
]);

/**
 * Keys of `profileData` that only an immigration practice can ever fill. They are
 * excluded from the completeness denominator for other disciplines, which otherwise
 * measured every adviser against an immigration-shaped target they could not reach.
 * `nationality`, `countryOfBirth` and `timeline` are deliberately NOT here — they are
 * ordinary client facts any adviser may record.
 */
const IMMIGRATION_ONLY_PROFILE_FIELDS = new Set([
  'targetCountry', 'immigrationCategory', 'currentStatus', 'visaStatus',
  'expressEntryProfile', 'ecaStatus',
  'passportNumber', 'passportExpiry', 'passportCountry',
  'canEnglish', 'canFrench', 'languageTest',
  'refusalHistory', 'canadianWorkExperience', 'jobOffer', 'familyInTargetCountry',
]);

const isProfileValueFilled = (value) => {
  if (value === null || value === undefined || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  const text = String(value).trim().toLowerCase();
  if (!text) return false;
  return !PROFILE_PLACEHOLDERS.has(text);
};

const TOGGLEABLE_SECTIONS = [
  { key: 'clientProfile', label: 'Client Profile (all extracted fields)' },
  { key: 'widgetAssessments', label: 'Widget assessments (CRS / Ontario PNP)' },
  { key: 'actionItems', label: 'Action Items' },
  { key: 'missingInfo', label: 'Missing Information' },
  { key: 'clientFollowUps', label: 'Client follow-ups (questions sent to the client)' },
  { key: 'nextSteps', label: 'Next Steps' },
  { key: 'internalNotes', label: 'Internal Notes' },
  { key: 'clientCommunication', label: 'Client Communication' },
  { key: 'longTermOutlook', label: 'Long-term Outlook' },
];

// ============================================
// MAIN COMPONENT
// ============================================

const CaseDashboard = ({ threadId, conversation }) => {
  const axios = useAuthenticatedAxios();
  // Declared up here because the profile-completeness computation below depends on it.
  const { isImmigration } = useCapabilities();

  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedPathway, setSelectedPathway] = useState(null);
  const [caseResolutions, setCaseResolutions] = useState([]);
  const [updatingItem, setUpdatingItem] = useState(null);
  const [showResumeBuilder, setShowResumeBuilder] = useState(false);
  const [askClient, setAskClient] = useState(null);           // gaps[] for the composer, or null
  const [followUpsKey, setFollowUpsKey] = useState(0);

  // Destructure data for profile computation (needed before hooks that depend on profileData)
  const dataForProfile = data || {};
  const { summary, assessment, pathways, blockers, risks, actionItems, missingInformation, nextSteps, internalNotes, clientCommunication, longTermOutlook, firmStandardsCompliance, clientProfile: rawClientProfile, personalDetails } = dataForProfile;

  // Extract comprehensive client profile from various possible sources
  const clientProfile = rawClientProfile || personalDetails || {};

  // Merge in data from summary if not already present
  const profileData = useMemo(() => ({
    // Basic Info
    firstName: clientProfile.firstName || clientProfile.givenName || summary?.clientName?.split(' ')[0] || null,
    lastName: clientProfile.lastName || clientProfile.familyName || summary?.clientName?.split(' ').slice(1).join(' ') || null,
    fullName: clientProfile.fullName || summary?.clientName || null,
    
    // Contact
    email: clientProfile.email || summary?.email || null,
    phone: clientProfile.phone || clientProfile.phoneNumber || clientProfile.telephone || summary?.phone || null,
    phoneHome: clientProfile.phoneHome || null,
    phoneWork: clientProfile.phoneWork || null,
    
    // Personal Details
    dateOfBirth: clientProfile.dateOfBirth || clientProfile.dob || null,
    age: clientProfile.age || null,
    gender: clientProfile.gender || clientProfile.sex || null,
    maritalStatus: clientProfile.maritalStatus || clientProfile.married || null,
    nationality: clientProfile.nationality || clientProfile.citizenship || summary?.nationality || null,
    countryOfBirth: clientProfile.countryOfBirth || clientProfile.birthCountry || null,
    nativeLanguage: clientProfile.nativeLanguage || clientProfile.motherTongue || null,
    
    // Location
    currentLocation: clientProfile.currentLocation || clientProfile.location || summary?.currentLocation || null,
    currentCountry: clientProfile.currentCountry || clientProfile.countryOfResidence || null,
    currentCity: clientProfile.currentCity || clientProfile.city || null,
    address: clientProfile.address || null,
    
    // Employment
    occupation: clientProfile.occupation || clientProfile.currentOccupation || clientProfile.job || summary?.employmentStatus || null,
    employer: clientProfile.employer || clientProfile.company || null,
    workExperience: clientProfile.workExperience || null,
    workHistory: clientProfile.workHistory || null,
    
    // Education
    education: clientProfile.education || clientProfile.educationLevel || null,
    educationLevel: clientProfile.educationLevel || null,
    institution: clientProfile.institution || clientProfile.school || null,
    yearsOfEducation: clientProfile.yearsOfEducation || null,
    educationCountry: clientProfile.educationCountry || null,
    
    // Immigration Specific
    targetCountry: clientProfile.targetCountry || summary?.targetCountry || null,
    immigrationCategory: clientProfile.immigrationCategory || summary?.category || summary?.caseCategory || null,
    timeline: clientProfile.timeline || summary?.timeline || null,
    currentStatus: clientProfile.currentStatus || summary?.currentStatus || null,
    visaStatus: clientProfile.visaStatus || null,
    expressEntryProfile: clientProfile.expressEntryProfile || null,
    ecaStatus: clientProfile.ecaStatus || null,
    
    // Documents
    passportNumber: clientProfile.passportNumber || null,
    passportExpiry: clientProfile.passportExpiry || null,
    passportCountry: clientProfile.passportCountry || null,
    
    // Language
    canEnglish: clientProfile.canEnglish || clientProfile.englishAbility || null,
    canFrench: clientProfile.canFrench || clientProfile.frenchAbility || null,
    languageTest: clientProfile.languageTest || null,
    
    // Family
    familyMembers: clientProfile.familyMembers || clientProfile.dependents || null,
    spouse: clientProfile.spouse || clientProfile.partner || null,
    children: clientProfile.children || null,
    
    // Financial & Legal
    financialInfo: clientProfile.financialInfo || clientProfile.settlementFunds || summary?.financialSituation || null,
    criminalHistory: clientProfile.criminalHistory || null,
    refusalHistory: clientProfile.refusalHistory || null,
    healthInfo: clientProfile.healthInfo || summary?.healthStatus || null,
    
    // Immigration-specific extras
    canadianWorkExperience: clientProfile.canadianWorkExperience || null,
    jobOffer: clientProfile.jobOffer || null,
    familyInTargetCountry: clientProfile.familyInTargetCountry || null,
    referralSource: clientProfile.referralSource || null,
    additionalNotes: clientProfile.additionalNotes || null,
  }), [clientProfile, summary]);

  // Count how many profile fields are actually filled.
  //
  // The model does not leave unknown fields empty — it writes the absence into
  // the value: "Unknown", "Not provided", "Not established", "Not stated". Those
  // are non-empty strings, so a naive truthiness count reported "23 fields
  // collected" for a profile that was almost entirely blanks. For an adviser
  // judging whether a file is ready, overstating completeness is the wrong
  // direction to be wrong in, so placeholders are counted as missing.
  //
  // Deliberately NOT treated as placeholders: "none" and "no". Those are real
  // answers to questions like dependents or prior refusals.
  // The denominator must reflect what THIS discipline can actually collect. profileData
  // is built with a fixed key list that includes ~17 immigration-only fields, so a
  // financial adviser was being told "9 of 47 fields collected" against a target they
  // could never reach. Immigration keys only count for an immigration discipline.
  const totalProfileFields = Object.keys(profileData).filter(
    (k) => isImmigration || !IMMIGRATION_ONLY_PROFILE_FIELDS.has(k)
  ).length;
  const filledProfileFields = Object.entries(profileData).filter(
    ([k, v]) => (isImmigration || !IMMIGRATION_ONLY_PROFILE_FIELDS.has(k)) && isProfileValueFilled(v)
  ).length;
  const hasProfileData = filledProfileFields > 0;

  // Share report hook
  const {
    showShareModal,
    setShowShareModal,
    showCopiedToast,
    shareSections,
    toggleShareSection,
    selectAllSections,
    shareViaEmail,
    copyToClipboard,
    downloadAsHTML,
    nativeShare,
    printReport,
  } = useShareReport(data, caseResolutions, hasProfileData, profileData);

  // Display preferences hook
  const {
    showSettingsModal,
    setShowSettingsModal,
    displayPrefs,
    updateDisplayPref,
    resetDisplayPrefs,
  } = useDisplayPrefs(axios);

  // The widget assessments panel (CRS / Ontario PNP) only makes sense for an adviser
  // whose widget actually runs those questionnaires. For everyone else it could only
  // ever render empty, so hide both the panel AND its display toggle rather than
  // offering a switch that controls nothing.
  const { assessmentTemplates } = useWidgetConfig();
  const offersAssessments = (assessmentTemplates || []).length > 0;
  const visibleToggleableSections = React.useMemo(
    () => TOGGLEABLE_SECTIONS.filter(s => s.key !== 'widgetAssessments' || offersAssessments),
    [offersAssessments]
  );

  // Check if an item is resolved
  const isItemResolved = (section, itemText) => {
    return caseResolutions.some(r => r.section === section && r.itemText === itemText);
  };

  // Get resolution details for an item
  const getResolution = (section, itemText) => {
    return caseResolutions.find(r => r.section === section && r.itemText === itemText);
  };

  // Toggle resolution status
  const toggleResolution = async (section, itemText, statusType) => {
    const itemKey = `${section}-${itemText}`;
    setUpdatingItem(itemKey);
    
    try {
      if (isItemResolved(section, itemText)) {
        // Remove resolution
        await axios.delete(`/analysis/case-resolution/${threadId}`, {
          data: { section, itemText }
        });
        setCaseResolutions(prev => prev.filter(r => !(r.section === section && r.itemText === itemText)));
      } else {
        // Add resolution
        const response = await axios.post(`/analysis/case-resolution/${threadId}`, {
          section,
          itemText,
          status: statusType
        });
        if (response.data?.caseResolutions) {
          setCaseResolutions(response.data.caseResolutions);
        }
      }
    } catch (err) {
      console.error('Error toggling resolution:', err);
    } finally {
      setUpdatingItem(null);
    }
  };

  // Load cached data on mount
  useEffect(() => {
    if (threadId) {
      loadCachedData();
    }
  }, [threadId]);

  const loadCachedData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`/analysis/case-options/${threadId}`);
      if (response.data?.analysis) {
        setData(response.data.analysis);
        setLastUpdated(response.data.timestamp ? new Date(response.data.timestamp) : new Date());
        if (response.data.analysis.pathways?.length > 0) {
          setSelectedPathway(response.data.analysis.pathways[0]);
        }
        // Load case resolutions
        if (response.data.caseResolutions) {
          setCaseResolutions(response.data.caseResolutions);
        }
      }
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error('Error loading data:', err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const generateAnalysis = async () => {
    if (!conversation?.messages?.length) return;
    
    setIsRefreshing(true);
    try {
      const response = await axios.post('/analysis/case-options', {
        threadId,
        conversation: {
          messages: conversation.messages,
          users: conversation.users
        }
      });
      
      if (response.data?.analysis) {
        setData(response.data.analysis);
        setLastUpdated(new Date());
        if (response.data.analysis.pathways?.length > 0) {
          setSelectedPathway(response.data.analysis.pathways[0]);
        }
      }
    } catch (err) {
      console.error('Error generating analysis:', err);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Calculate compliance summary stats - must be before early returns
  const complianceStats = React.useMemo(() => {
    if (!data?.firmStandardsCompliance?.sections) return null;
    
    const sections = data.firmStandardsCompliance.sections;
    return {
      compliant: sections.filter(s => s.status === 'compliant').length,
      violations: sections.filter(s => s.status === 'violation').length,
      warnings: sections.filter(s => s.status === 'warning').length,
      notApplicable: sections.filter(s => s.status === 'not_applicable').length,
      total: sections.length
    };
  }, [data?.firmStandardsCompliance]);

  // Helper to get status icon
  const getStatusIcon = (status) => {
    switch(status) {
      case 'compliant':
        return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 13l4 4L19 7" /></svg>;
      case 'violation':
        return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 18L18 6M6 6l12 12" /></svg>;
      case 'warning':
        return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>;
      default:
        return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 12H4" /></svg>;
    }
  };

  const getStatusLabel = (status) => {
    switch(status) {
      case 'compliant': return 'Compliant';
      case 'violation': return 'Violation';
      case 'warning': return 'Needs Review';
      case 'not_applicable': return 'N/A';
      default: return 'Unknown';
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Container>
        <EmptyState>
          <LoadingSpinner $size={48} />
          <p style={{ marginTop: 16 }}>Loading case data...</p>
        </EmptyState>
      </Container>
    );
  }

  // Empty state - no data yet
  if (!data) {
    // Show loading screen when analyzing
    if (isRefreshing) {
      return <AnalysisLoadingScreen visible={true} />;
    }
    
    return (
      <Container>
        <EmptyState>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3>Ready to Analyze</h3>
          <p>Click below to generate a comprehensive case assessment including client summary, immigration pathways, and action items.</p>
          <AnalyzeButton onClick={generateAnalysis} disabled={isRefreshing || !conversation?.messages?.length}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Analyze Case
          </AnalyzeButton>
          <button
            type="button"
            onClick={() => setShowResumeBuilder(true)}
            style={{ marginTop: 12, background: 'none', border: 'none', color: '#86efac', cursor: 'pointer', fontSize: 13 }}
          >
            or make a resume for this client →
          </button>
        </EmptyState>
        {showResumeBuilder && (
          <ResumeBuilder threadId={threadId} clientName={summary?.clientName} onClose={() => setShowResumeBuilder(false)} />
        )}
      </Container>
    );
  }

  return (
    <Container>
      {/* Header */}
      <Header>
        <HeaderLeft>
          <HeaderTitle>
            Case Dashboard
            {summary?.caseCategory && <HeaderBadge>{summary.caseCategory}</HeaderBadge>}
          </HeaderTitle>
          <HeaderSubtitle>
            {summary?.clientName && <span>Client: {summary.clientName}</span>}
            {pathways?.length > 0 && <span>• {pathways.length} pathways identified</span>}
          </HeaderSubtitle>
        </HeaderLeft>
        <HeaderRight>
          {assessment && (
            <StatusBadge $status={assessment.statusIndicator}>
              <div className="indicator" />
              <span>
                {assessment.statusIndicator === 'green' ? 'Good Standing' : 
                 assessment.statusIndicator === 'yellow' ? 'Needs Attention' : 'Critical Issues'}
              </span>
            </StatusBadge>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            <SettingsButton onClick={() => setShowSettingsModal(true)} title="Display Settings">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </SettingsButton>
            <ShareButton onClick={() => setShowResumeBuilder(true)} title="Build a Canadian-format resume for this client">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                <circle cx="10" cy="8" r="1.5" />
              </svg>
              Make Resume
            </ShareButton>
            <ShareButton onClick={() => setShowShareModal(true)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              Share
            </ShareButton>
            <RefreshButton onClick={generateAnalysis} disabled={isRefreshing}>
              {isRefreshing ? (
                <SpinningIcon viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                  <path d="M12 2a10 10 0 0 1 10 10" />
                </SpinningIcon>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              )}
              {isRefreshing ? 'Analyzing...' : 'Refresh Analysis'}
            </RefreshButton>
          </div>
          {lastUpdated && <LastUpdated>Updated: {lastUpdated.toLocaleTimeString()}</LastUpdated>}
        </HeaderRight>
      </Header>

      <MainContent>
        {/* Client Profile - Comprehensive Personal Data */}
        {displayPrefs.clientProfile && hasProfileData && (
          <Section>
            <SectionHeader>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              <h4>Client Profile</h4>
              <span style={{ marginLeft: 'auto', fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                {filledProfileFields} of {totalProfileFields} fields collected
              </span>
            </SectionHeader>
            <SectionContent>
              <ProfileGrid>
                {/* Identity */}
                {(profileData.fullName || profileData.firstName) && (
                  <ProfileField className="highlight">
                    <ProfileLabel>Full Name</ProfileLabel>
                    <ProfileValue>
                      {profileData.fullName || `${profileData.firstName || ''} ${profileData.lastName || ''}`.trim() || '—'}
                    </ProfileValue>
                  </ProfileField>
                )}
                
                {/* Contact Info - Important */}
                {profileData.email && (
                  <ProfileField className="contact">
                    <ProfileLabel>Email</ProfileLabel>
                    <ProfileValue className="email">{profileData.email}</ProfileValue>
                  </ProfileField>
                )}
                
                {profileData.phone && (
                  <ProfileField className="contact">
                    <ProfileLabel>Phone</ProfileLabel>
                    <ProfileValue className="phone">{profileData.phone}</ProfileValue>
                  </ProfileField>
                )}
                
                {profileData.phoneHome && (
                  <ProfileField className="contact">
                    <ProfileLabel>Home Phone</ProfileLabel>
                    <ProfileValue className="phone">{profileData.phoneHome}</ProfileValue>
                  </ProfileField>
                )}
                
                {/* Personal Details */}
                {profileData.nationality && (
                  <ProfileField>
                    <ProfileLabel>Nationality / Citizenship</ProfileLabel>
                    <ProfileValue>{profileData.nationality}</ProfileValue>
                  </ProfileField>
                )}
                
                {profileData.dateOfBirth && (
                  <ProfileField>
                    <ProfileLabel>Date of Birth</ProfileLabel>
                    <ProfileValue>{profileData.dateOfBirth}</ProfileValue>
                  </ProfileField>
                )}
                
                {profileData.gender && (
                  <ProfileField>
                    <ProfileLabel>Gender</ProfileLabel>
                    <ProfileValue>{profileData.gender}</ProfileValue>
                  </ProfileField>
                )}
                
                {profileData.maritalStatus && (
                  <ProfileField>
                    <ProfileLabel>Marital Status</ProfileLabel>
                    <ProfileValue>{profileData.maritalStatus}</ProfileValue>
                  </ProfileField>
                )}
                
                {profileData.countryOfBirth && (
                  <ProfileField>
                    <ProfileLabel>Country of Birth</ProfileLabel>
                    <ProfileValue>{profileData.countryOfBirth}</ProfileValue>
                  </ProfileField>
                )}
                
                {/* Location */}
                {(profileData.currentLocation || profileData.currentCity || profileData.currentCountry) && (
                  <ProfileField>
                    <ProfileLabel>Current Location</ProfileLabel>
                    <ProfileValue>
                      {profileData.currentLocation || [profileData.currentCity, profileData.currentCountry].filter(Boolean).join(', ') || '—'}
                    </ProfileValue>
                  </ProfileField>
                )}
                
                {profileData.address && (
                  <ProfileField>
                    <ProfileLabel>Address</ProfileLabel>
                    <ProfileValue>{typeof profileData.address === 'string' ? profileData.address : JSON.stringify(profileData.address)}</ProfileValue>
                  </ProfileField>
                )}
                
                {/* Employment */}
                {profileData.occupation && (
                  <ProfileField>
                    <ProfileLabel>Occupation</ProfileLabel>
                    <ProfileValue>{profileData.occupation}</ProfileValue>
                  </ProfileField>
                )}
                
                {profileData.employer && (
                  <ProfileField>
                    <ProfileLabel>Employer</ProfileLabel>
                    <ProfileValue>{profileData.employer}</ProfileValue>
                  </ProfileField>
                )}
                
                {/* Education */}
                {profileData.education && (
                  <ProfileField>
                    <ProfileLabel>Education</ProfileLabel>
                    <ProfileValue>{profileData.education}</ProfileValue>
                  </ProfileField>
                )}
                
                {profileData.institution && (
                  <ProfileField>
                    <ProfileLabel>Institution</ProfileLabel>
                    <ProfileValue>{profileData.institution}</ProfileValue>
                  </ProfileField>
                )}
                
                {/* Immigration */}
                {profileData.targetCountry && (
                  <ProfileField className="highlight">
                    <ProfileLabel>Target Country</ProfileLabel>
                    <ProfileValue>{profileData.targetCountry}</ProfileValue>
                  </ProfileField>
                )}
                
                {profileData.immigrationCategory && (
                  <ProfileField>
                    <ProfileLabel>Immigration Category</ProfileLabel>
                    <ProfileValue>{profileData.immigrationCategory}</ProfileValue>
                  </ProfileField>
                )}
                
                {profileData.timeline && (
                  <ProfileField>
                    <ProfileLabel>Timeline</ProfileLabel>
                    <ProfileValue>{profileData.timeline}</ProfileValue>
                  </ProfileField>
                )}
                
                {profileData.currentStatus && (
                  <ProfileField>
                    <ProfileLabel>Current Status</ProfileLabel>
                    <ProfileValue>{profileData.currentStatus}</ProfileValue>
                  </ProfileField>
                )}
                
                {profileData.visaStatus && (
                  <ProfileField>
                    <ProfileLabel>Visa Status</ProfileLabel>
                    <ProfileValue>{profileData.visaStatus}</ProfileValue>
                  </ProfileField>
                )}
                
                {/* Language */}
                {profileData.nativeLanguage && (
                  <ProfileField>
                    <ProfileLabel>Native Language</ProfileLabel>
                    <ProfileValue>{profileData.nativeLanguage}</ProfileValue>
                  </ProfileField>
                )}
                
                {profileData.canEnglish && (
                  <ProfileField>
                    <ProfileLabel>English</ProfileLabel>
                    <ProfileValue>{profileData.canEnglish === true || profileData.canEnglish === 'Yes' ? 'Yes' : profileData.canEnglish}</ProfileValue>
                  </ProfileField>
                )}
                
                {profileData.canFrench && (
                  <ProfileField>
                    <ProfileLabel>French</ProfileLabel>
                    <ProfileValue>{profileData.canFrench === true || profileData.canFrench === 'Yes' ? 'Yes' : profileData.canFrench}</ProfileValue>
                  </ProfileField>
                )}
                
                {profileData.languageTest && (
                  <ProfileField>
                    <ProfileLabel>Language Test</ProfileLabel>
                    <ProfileValue>{typeof profileData.languageTest === 'string' ? profileData.languageTest : JSON.stringify(profileData.languageTest)}</ProfileValue>
                  </ProfileField>
                )}
                
                {/* Passport */}
                {profileData.passportNumber && (
                  <ProfileField>
                    <ProfileLabel>Passport Number</ProfileLabel>
                    <ProfileValue>{profileData.passportNumber}</ProfileValue>
                  </ProfileField>
                )}
                
                {profileData.passportExpiry && (
                  <ProfileField>
                    <ProfileLabel>Passport Expiry</ProfileLabel>
                    <ProfileValue>{profileData.passportExpiry}</ProfileValue>
                  </ProfileField>
                )}
                
                {profileData.passportCountry && (
                  <ProfileField>
                    <ProfileLabel>Passport Country</ProfileLabel>
                    <ProfileValue>{profileData.passportCountry}</ProfileValue>
                  </ProfileField>
                )}
                
                {/* Family */}
                {profileData.familyMembers && (
                  <ProfileField>
                    <ProfileLabel>Family Members / Dependents</ProfileLabel>
                    <ProfileValue>{typeof profileData.familyMembers === 'number' ? profileData.familyMembers : profileData.familyMembers}</ProfileValue>
                  </ProfileField>
                )}
                
                {profileData.spouse && (
                  <ProfileField>
                    <ProfileLabel>Spouse / Partner</ProfileLabel>
                    <ProfileValue>{typeof profileData.spouse === 'string' ? profileData.spouse : profileData.spouse.name || 'Yes'}</ProfileValue>
                  </ProfileField>
                )}

                {profileData.children && (
                  <ProfileField>
                    <ProfileLabel>Children</ProfileLabel>
                    <ProfileValue>{profileData.children}</ProfileValue>
                  </ProfileField>
                )}

                {/* Age */}
                {profileData.age && (
                  <ProfileField>
                    <ProfileLabel>Age</ProfileLabel>
                    <ProfileValue>{profileData.age}</ProfileValue>
                  </ProfileField>
                )}

                {/* Work Experience Summary */}
                {profileData.workExperience && (
                  <ProfileField>
                    <ProfileLabel>Work Experience</ProfileLabel>
                    <ProfileValue>{profileData.workExperience}</ProfileValue>
                  </ProfileField>
                )}

                {/* Work History (array of jobs) */}
                {Array.isArray(profileData.workHistory) && profileData.workHistory.length > 0 && (
                  <ProfileField style={{ gridColumn: '1 / -1' }}>
                    <ProfileLabel>Employment History</ProfileLabel>
                    <ProfileValue>
                      {profileData.workHistory.map((job, i) => (
                        <div key={i} style={{ marginBottom: i < profileData.workHistory.length - 1 ? 8 : 0, paddingBottom: i < profileData.workHistory.length - 1 ? 8 : 0, borderBottom: i < profileData.workHistory.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                          <strong>{job.title || 'Position'}</strong>
                          {job.employer && <span> at {job.employer}</span>}
                          {(job.country || job.startDate || job.endDate || job.duration) && (
                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>
                              {job.country && <span>{job.country} · </span>}
                              {job.startDate && <span>{job.startDate}</span>}
                              {job.endDate && <span> – {job.endDate}</span>}
                              {job.duration && <span> ({job.duration})</span>}
                            </div>
                          )}
                          {job.responsibilities && (
                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>{job.responsibilities}</div>
                          )}
                        </div>
                      ))}
                    </ProfileValue>
                  </ProfileField>
                )}

                {/* Education extras */}
                {profileData.educationLevel && !profileData.education && (
                  <ProfileField>
                    <ProfileLabel>Education Level</ProfileLabel>
                    <ProfileValue>{profileData.educationLevel}</ProfileValue>
                  </ProfileField>
                )}

                {profileData.educationCountry && (
                  <ProfileField>
                    <ProfileLabel>Education Country</ProfileLabel>
                    <ProfileValue>{profileData.educationCountry}</ProfileValue>
                  </ProfileField>
                )}

                {/* Financial & Legal */}
                {profileData.financialInfo && (
                  <ProfileField>
                    <ProfileLabel>Financial Information</ProfileLabel>
                    <ProfileValue>{profileData.financialInfo}</ProfileValue>
                  </ProfileField>
                )}

                {profileData.criminalHistory && (
                  <ProfileField>
                    <ProfileLabel>Criminal History</ProfileLabel>
                    <ProfileValue>{profileData.criminalHistory}</ProfileValue>
                  </ProfileField>
                )}

                {profileData.refusalHistory && (
                  <ProfileField>
                    <ProfileLabel>Refusal History</ProfileLabel>
                    <ProfileValue>{profileData.refusalHistory}</ProfileValue>
                  </ProfileField>
                )}

                {profileData.healthInfo && (
                  <ProfileField>
                    <ProfileLabel>Health Information</ProfileLabel>
                    <ProfileValue>{profileData.healthInfo}</ProfileValue>
                  </ProfileField>
                )}

                {/* Immigration extras */}
                {profileData.expressEntryProfile && (
                  <ProfileField>
                    <ProfileLabel>Express Entry Profile</ProfileLabel>
                    <ProfileValue>{profileData.expressEntryProfile}</ProfileValue>
                  </ProfileField>
                )}

                {profileData.ecaStatus && (
                  <ProfileField>
                    <ProfileLabel>ECA Status</ProfileLabel>
                    <ProfileValue>{profileData.ecaStatus}</ProfileValue>
                  </ProfileField>
                )}

                {profileData.canadianWorkExperience && (
                  <ProfileField>
                    <ProfileLabel>Canadian Work Experience</ProfileLabel>
                    <ProfileValue>{profileData.canadianWorkExperience}</ProfileValue>
                  </ProfileField>
                )}

                {profileData.jobOffer && (
                  <ProfileField>
                    <ProfileLabel>Job Offer</ProfileLabel>
                    <ProfileValue>{profileData.jobOffer}</ProfileValue>
                  </ProfileField>
                )}

                {profileData.familyInTargetCountry && (
                  <ProfileField>
                    <ProfileLabel>Family in Target Country</ProfileLabel>
                    <ProfileValue>{profileData.familyInTargetCountry}</ProfileValue>
                  </ProfileField>
                )}

                {profileData.referralSource && (
                  <ProfileField>
                    <ProfileLabel>Referral Source</ProfileLabel>
                    <ProfileValue>{profileData.referralSource}</ProfileValue>
                  </ProfileField>
                )}

                {profileData.additionalNotes && (
                  <ProfileField style={{ gridColumn: '1 / -1' }}>
                    <ProfileLabel>Additional Notes</ProfileLabel>
                    <ProfileValue>{profileData.additionalNotes}</ProfileValue>
                  </ProfileField>
                )}
              </ProfileGrid>
            </SectionContent>
          </Section>
        )}

        {/* Client Summary */}
        {displayPrefs.clientSummary && summary && (
          <Section>
            <SectionHeader>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <h4>Client Summary</h4>
            </SectionHeader>
            <SectionContent>
              <TwoColumnGrid>
                <InfoCard>
                  <CardLabel>Profile</CardLabel>
                  {/* Discipline is read from the capability profile, NOT inferred.
                      This card used to switch shape on whether `summary.nationality`
                      happened to be present — so an analysis that simply did not extract
                      a nationality silently flipped an immigration case into the generic
                      layout, and hid Category/Context from immigration advisers who did
                      have one. Both halves now render on an explicit flag. */}
                  {summary.nationality && <CardValue><strong>Nationality:</strong> {summary.nationality}</CardValue>}
                  {summary.currentStatus && <CardValue><strong>Current Status:</strong> {summary.currentStatus}</CardValue>}
                  {summary.currentLocation && <CardValue><strong>Location:</strong> {summary.currentLocation}</CardValue>}
                  {summary.targetCountry && <CardValue><strong>Target:</strong> {summary.targetCountry}</CardValue>}
                  {summary.timeline && <CardValue><strong>Timeline:</strong> {summary.timeline}</CardValue>}

                  {summary.clientName && <CardValue><strong>Client:</strong> {summary.clientName}</CardValue>}
                  {/* Category/Context are the general directive's framing fields. Shown for
                      any non-immigration discipline, and for immigration only when the
                      immigration-specific framing is absent, preserving today's layout. */}
                  {summary.category && (!isImmigration || !summary.targetCountry) && <CardValue><strong>Category:</strong> {summary.category}</CardValue>}
                  {summary.context && (!isImmigration || !summary.targetCountry) && <CardValue><strong>Context:</strong> {summary.context}</CardValue>}
                  {summary.financialSituation && <CardValue><strong>Financial Situation:</strong> {summary.financialSituation}</CardValue>}
                  {summary.healthStatus && <CardValue><strong>Health Status:</strong> {summary.healthStatus}</CardValue>}
                  {summary.employmentStatus && <CardValue><strong>Employment:</strong> {summary.employmentStatus}</CardValue>}
                  {summary.budget && <CardValue><strong>Budget:</strong> {summary.budget}</CardValue>}
                  {summary.goals && <CardValue><strong>Goals:</strong> {summary.goals}</CardValue>}

                  {/* Fallback if no specific profile info found */}
                  {!summary.nationality && !summary.clientName && !summary.category && !summary.context && !summary.financialSituation && !summary.healthStatus && (
                    <CardValue style={{ color: 'rgba(255,255,255,0.5)', fontStyle: 'italic' }}>Profile information not available</CardValue>
                  )}
                </InfoCard>
                <InfoCard>
                  <CardLabel>Objective</CardLabel>
                  <CardValue>{summary.objective || 'Not specified'}</CardValue>
                  {summary.keyFacts?.length > 0 && (
                    <>
                      <CardLabel style={{ marginTop: 12 }}>Key Facts</CardLabel>
                      <ul style={{ margin: 0, paddingLeft: 16 }}>
                        {summary.keyFacts.slice(0, 5).map((fact, i) => (
                          <li key={i} style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', padding: '2px 0' }}>{fact}</li>
                        ))}
                      </ul>
                    </>
                  )}
                </InfoCard>
              </TwoColumnGrid>
            </SectionContent>
          </Section>
        )}

        {/* Quick Stats */}
        {displayPrefs.quickStats && (blockers?.length > 0 || risks?.length > 0 || nextSteps?.length > 0) && (
          <ThreeColumnGrid style={{ marginBottom: 20 }}>
            <QuickStatCard>
              <h5>Top Blockers ({blockers?.filter(b => !isItemResolved('blockers', b)).length}/{blockers?.length || 0})</h5>
              {blockers?.length > 0 ? (
                <BlockerList>
                  {blockers.slice(0, 5).map((b, i) => {
                    const blockerResolved = isItemResolved('blockers', b);
                    const isUpdating = updatingItem === `blockers-${b}`;
                    return (
                      <li key={i} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        textDecoration: blockerResolved ? 'line-through' : 'none',
                        opacity: blockerResolved ? 0.6 : 1
                      }}>
                        <span style={{ flex: 1 }}>{b}</span>
                        <ResolutionCheckbox 
                          $checked={blockerResolved}
                          onClick={() => toggleResolution('blockers', b, 'resolved')}
                          disabled={isUpdating}
                          title={blockerResolved ? 'Mark as unresolved' : 'Mark as resolved'}
                          style={{ width: 22, height: 22, marginLeft: 6 }}
                        >
                          {isUpdating ? (
                            <div className="spinner" style={{ width: 10, height: 10 }} />
                          ) : (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                              <polyline points="20,6 9,17 4,12" />
                            </svg>
                          )}
                        </ResolutionCheckbox>
                      </li>
                    );
                  })}
                </BlockerList>
              ) : (
                <div style={{ color: '#34d399', fontSize: 12 }}>No blockers found</div>
              )}
            </QuickStatCard>
            
            <QuickStatCard>
              <h5>Key Risks ({risks?.filter(r => !isItemResolved('risks', r)).length}/{risks?.length || 0})</h5>
              {risks?.length > 0 ? (
                <RiskList>
                  {risks.slice(0, 5).map((r, i) => {
                    const riskResolved = isItemResolved('risks', r);
                    const isUpdating = updatingItem === `risks-${r}`;
                    return (
                      <li key={i} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        textDecoration: riskResolved ? 'line-through' : 'none',
                        opacity: riskResolved ? 0.6 : 1
                      }}>
                        <span style={{ flex: 1 }}>{r}</span>
                        <ResolutionCheckbox 
                          $checked={riskResolved}
                          onClick={() => toggleResolution('risks', r, 'mitigated')}
                          disabled={isUpdating}
                          title={riskResolved ? 'Mark as active' : 'Mark as mitigated'}
                          style={{ width: 22, height: 22, marginLeft: 6 }}
                        >
                          {isUpdating ? (
                            <div className="spinner" style={{ width: 10, height: 10 }} />
                          ) : (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                              <polyline points="20,6 9,17 4,12" />
                            </svg>
                          )}
                        </ResolutionCheckbox>
                      </li>
                    );
                  })}
                </RiskList>
              ) : (
                <div style={{ color: '#34d399', fontSize: 12 }}>No risks identified</div>
              )}
            </QuickStatCard>
            
            <QuickStatCard>
              <h5>Overall Readiness</h5>
              {assessment && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <ScoreCircle $score={assessment.overallReadiness}>{assessment.overallReadiness}</ScoreCircle>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>
                    Confidence: <strong style={{ color: assessment.overallConfidence === 'high' ? '#34d399' : assessment.overallConfidence === 'medium' ? '#fbbf24' : '#f87171' }}>
                      {assessment.overallConfidence?.toUpperCase()}
                    </strong>
                  </div>
                </div>
              )}
            </QuickStatCard>
          </ThreeColumnGrid>
        )}

        {/* Firm Standards Compliance */}
        {displayPrefs.firmCompliance && firmStandardsCompliance?.sections?.length > 0 && (
          <Section>
            <SectionHeader>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <h4>Firm Standards Compliance</h4>
            </SectionHeader>
            <SectionContent>
              {/* Summary Bar */}
              {complianceStats && (
                <ComplianceSummaryBar>
                  <ComplianceStat $type="compliant">
                    <div className="icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div className="info">
                      <div className="count">{complianceStats.compliant}</div>
                      <div className="label">Compliant</div>
                    </div>
                  </ComplianceStat>
                  
                  <ComplianceStat $type="violation">
                    <div className="icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                    <div className="info">
                      <div className="count">{complianceStats.violations}</div>
                      <div className="label">Violations</div>
                    </div>
                  </ComplianceStat>
                  
                  <ComplianceStat $type="warning">
                    <div className="icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="info">
                      <div className="count">{complianceStats.warnings}</div>
                      <div className="label">Warnings</div>
                    </div>
                  </ComplianceStat>
                  
                  <ComplianceStat $type="na">
                    <div className="icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 12H4" />
                      </svg>
                    </div>
                    <div className="info">
                      <div className="count">{complianceStats.notApplicable}</div>
                      <div className="label">Not Applicable</div>
                    </div>
                  </ComplianceStat>
                </ComplianceSummaryBar>
              )}
              
              {/* Overall Assessment */}
              {firmStandardsCompliance.overallAssessment && (
                <div style={{ marginBottom: 16, padding: '12px 16px', background: 'rgba(99, 102, 241, 0.08)', borderRadius: 8, borderLeft: '3px solid #6366f1' }}>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', marginBottom: 6 }}>Overall Assessment</div>
                  <div style={{ fontSize: 13, color: '#e8eaed', lineHeight: 1.5 }}>{firmStandardsCompliance.overallAssessment}</div>
                </div>
              )}
              
              {/* Compliance Cards Grid */}
              <ComplianceGrid>
                {firmStandardsCompliance.sections.map((section, idx) => (
                  <ComplianceCard key={idx} $status={section.status}>
                    <ComplianceHeader>
                      <ComplianceSectionName $status={section.status}>
                        {section.sectionName}
                      </ComplianceSectionName>
                      <ComplianceStatusBadge $status={section.status}>
                        {getStatusIcon(section.status)}
                        {getStatusLabel(section.status)}
                      </ComplianceStatusBadge>
                    </ComplianceHeader>
                    
                    {section.summary && (
                      <ComplianceDetail>{section.summary}</ComplianceDetail>
                    )}
                    
                    {section.issues?.length > 0 && (
                      <ComplianceIssuesList $isWarning={section.status === 'warning'}>
                        {section.issues.map((issue, i) => (
                          <li key={i}>{issue}</li>
                        ))}
                      </ComplianceIssuesList>
                    )}
                    
                    {section.recommendation && (
                      <div style={{ marginTop: 8, fontSize: 11, color: '#a5b4fc', fontStyle: 'italic' }}>
                        💡 {section.recommendation}
                      </div>
                    )}
                  </ComplianceCard>
                ))}
              </ComplianceGrid>
              
              {/* Critical Issues Summary */}
              {firmStandardsCompliance.criticalIssues?.length > 0 && (
                <div style={{ marginTop: 16, padding: '12px 16px', background: 'rgba(239, 68, 68, 0.1)', borderRadius: 8, border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                  <div style={{ fontSize: 11, color: '#f87171', textTransform: 'uppercase', marginBottom: 8, fontWeight: 600 }}>⚠️ Critical Issues Requiring Attention</div>
                  <ul style={{ margin: 0, padding: '0 0 0 16px' }}>
                    {firmStandardsCompliance.criticalIssues.map((issue, i) => (
                      <li key={i} style={{ fontSize: 12, color: '#fca5a5', padding: '3px 0' }}>{issue}</li>
                    ))}
                  </ul>
                </div>
              )}
            </SectionContent>
          </Section>
        )}

        {/* Immigration Pathways */}
        {displayPrefs.pathways && pathways?.length > 0 && (
          <Section>
            <SectionHeader>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2z" />
              </svg>
              <h4>Immigration Pathways ({pathways.length})</h4>
            </SectionHeader>
            <SectionContent>
              <PathwaysGrid>
                {pathways.map((pathway, idx) => (
                  <PathwayCard key={idx} $score={pathway.score} $selected={selectedPathway?.name === pathway.name}
                    onClick={() => setSelectedPathway(pathway)}>
                    <PathwayHeader>
                      <PathwayTitle>
                        <span className="flag">{getCountryFlag(pathway.country)}</span>
                        <div>
                          <div className="country">{pathway.country}</div>
                          <h5>{pathway.name}</h5>
                        </div>
                      </PathwayTitle>
                      <ScoreCircle $score={pathway.score}>{pathway.score}</ScoreCircle>
                    </PathwayHeader>
                    <PathwaySummary>{pathway.summary}</PathwaySummary>
                    <ConfidenceBadge $level={pathway.confidence?.toLowerCase()}>
                      Confidence: {pathway.confidence}
                    </ConfidenceBadge>
                    <ProsCons>
                      <div className="pros">
                        <h6>Strengths</h6>
                        <ul>{pathway.strengths?.slice(0, 3).map((s, i) => <li key={i}>{s}</li>)}</ul>
                      </div>
                      <div className="cons">
                        <h6>Concerns</h6>
                        <ul>{pathway.concerns?.slice(0, 3).map((c, i) => <li key={i}>{c}</li>)}</ul>
                      </div>
                    </ProsCons>
                  </PathwayCard>
                ))}
              </PathwaysGrid>

              {/* Score Breakdown */}
              {selectedPathway?.scoreBreakdown && (
                <div style={{ marginTop: 20 }}>
                  <CardLabel>Score Breakdown: {selectedPathway.name}</CardLabel>
                  <BreakdownGrid style={{ marginTop: 10 }}>
                    {Object.entries(selectedPathway.scoreBreakdown).map(([key, score]) => (
                      <BreakdownItem key={key} $score={score}>
                        <div className="label">
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}
                          <span>{score}%</span>
                        </div>
                        <div className="bar"><div className="fill" /></div>
                      </BreakdownItem>
                    ))}
                  </BreakdownGrid>
                </div>
              )}
            </SectionContent>
          </Section>
        )}

        {/* Action Items */}
        {displayPrefs.actionItems && actionItems?.length > 0 && (
          <Section>
            <SectionHeader>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h4>Action Items ({actionItems.length})</h4>
            </SectionHeader>
            <SectionContent>
              <ActionItemsList>
                {actionItems.map((item, idx) => (
                  <ActionItem key={idx} $priority={item.priority}>
                    <span className="text">{item.action}</span>
                    <span className="priority">{item.priority}</span>
                  </ActionItem>
                ))}
              </ActionItemsList>
            </SectionContent>
          </Section>
        )}

        {/* Missing Information */}
        {displayPrefs.missingInfo && missingInformation?.length > 0 && (
          <Section>
            <SectionHeader>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
                <rect x="9" y="3" width="6" height="4" rx="2" />
              </svg>
              <h4>Missing Information ({missingInformation.filter(item => !isItemResolved('missingDocuments', item.item)).length}/{missingInformation.length} pending)</h4>
              {missingInformation.some(item => !isItemResolved('missingDocuments', item.item)) && (
                <ShareButton
                  style={{ marginLeft: 'auto', padding: '6px 12px' }}
                  title="Email the client a short questionnaire for the pending items — answers come back into this case"
                  onClick={() => setAskClient(
                    missingInformation
                      .filter(item => !isItemResolved('missingDocuments', item.item))
                      .map(item => item.question || item.item)
                  )}
                >
                  Ask the client
                </ShareButton>
              )}
            </SectionHeader>
            <SectionContent>
              <MissingItemsGrid>
                {missingInformation.map((item, idx) => {
                  const itemResolved = isItemResolved('missingDocuments', item.item);
                  const isUpdating = updatingItem === `missingDocuments-${item.item}`;
                  return (
                    <MissingItem key={idx} $priority={item.priority} $resolved={itemResolved}>
                      <div className="checkbox-col">
                        <ResolutionCheckbox 
                          $checked={itemResolved}
                          onClick={() => toggleResolution('missingDocuments', item.item, 'received')}
                          disabled={isUpdating}
                          title={itemResolved ? 'Mark as not received' : 'Mark as received'}
                        >
                          {isUpdating ? (
                            <div className="spinner" />
                          ) : (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                              <polyline points="20,6 9,17 4,12" />
                            </svg>
                          )}
                        </ResolutionCheckbox>
                      </div>
                      <div className="content-col">
                        <div className="top-row">
                          <span className="priority">{itemResolved ? '✓ received' : item.priority}</span>
                          <span className="name">{item.item}</span>
                        </div>
                        {item.question && !itemResolved && (
                          <div className="question">Ask: "{item.question}"</div>
                        )}
                        {itemResolved && (
                          <div className="question" style={{ color: '#34d399' }}>Document has been received</div>
                        )}
                      </div>
                    </MissingItem>
                  );
                })}
              </MissingItemsGrid>
            </SectionContent>
          </Section>
        )}

        {/* Client follow-ups — questions emailed to the client, with their answers */}
        {displayPrefs.clientFollowUps !== false && (
          <Section>
            <SectionHeader>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M8 10h8M8 14h5M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4-.84L3 20l1.4-3.72A7.56 7.56 0 013 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <h4>Client follow-ups</h4>
            </SectionHeader>
            <SectionContent>
              <InfoRequestsPanel threadId={threadId} refreshKey={followUpsKey} onAsk={() => setAskClient([])} />
            </SectionContent>
          </Section>
        )}

        {/* Next Steps */}
        {displayPrefs.nextSteps && nextSteps?.length > 0 && (
          <Section>
            <SectionHeader>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 5l7 7-7 7" />
              </svg>
              <h4>Next Steps</h4>
            </SectionHeader>
            <SectionContent>
              <NextStepsList>
                {nextSteps.map((step, idx) => (
                  <NextStep key={idx}>
                    <div className="number">{step.step || idx + 1}</div>
                    <div className="content">
                      <div className="description">{step.description}</div>
                      {step.timeframe && <div className="timeframe">{step.timeframe}</div>}
                    </div>
                  </NextStep>
                ))}
              </NextStepsList>
            </SectionContent>
          </Section>
        )}

        {/* Internal Notes */}
        {displayPrefs.internalNotes && internalNotes?.length > 0 && (
          <Section>
            <SectionHeader>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14.5 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V7.5L14.5 2z" />
                <polyline points="14,2 14,8 20,8" />
              </svg>
              <h4>Internal Notes</h4>
            </SectionHeader>
            <SectionContent>
              {internalNotes.map((note, idx) => (
                <NoteItem key={idx}>{note}</NoteItem>
              ))}
            </SectionContent>
          </Section>
        )}

        {/* Professional Advice */}
        {displayPrefs.professionalAdvice && data?.professionalAdvice && (
          <Section>
            <SectionHeader>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 14l9-5-9-5-9 5 9 5z" />
                <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                <path d="M12 14l9-5-9-5-9 5 9 5zm0 0v6" />
              </svg>
              <h4>Professional Advice</h4>
            </SectionHeader>
            <SectionContent>
              {/* Expert Analysis */}
              {data.professionalAdvice.expertAnalysis && (
                <div style={{ marginBottom: 20 }}>
                  <CardLabel style={{ marginBottom: 8 }}>Expert Analysis</CardLabel>
                  <CardValue style={{ 
                    background: 'rgba(99, 102, 241, 0.1)', 
                    padding: 16, 
                    borderRadius: 8,
                    borderLeft: '4px solid #6366f1',
                    lineHeight: 1.6
                  }}>
                    {data.professionalAdvice.expertAnalysis}
                  </CardValue>
                </div>
              )}
              
              {/* Strategic Recommendations */}
              {data.professionalAdvice.strategicRecommendations?.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <CardLabel style={{ marginBottom: 8 }}>Strategic Recommendations</CardLabel>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {data.professionalAdvice.strategicRecommendations.map((rec, idx) => (
                      <div key={idx} style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10,
                        padding: '10px 14px',
                        background: 'rgba(34, 197, 94, 0.08)',
                        borderRadius: 8,
                        border: '1px solid rgba(34, 197, 94, 0.2)'
                      }}>
                        <span style={{ color: '#22c55e', fontWeight: 600 }}>★</span>
                        <span style={{ fontSize: 13, color: '#e8eaed', lineHeight: 1.5 }}>{rec}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Risk Considerations */}
              {data.professionalAdvice.riskConsiderations && (
                <div style={{ marginBottom: 20 }}>
                  <CardLabel style={{ marginBottom: 8 }}>Risk Considerations</CardLabel>
                  <CardValue style={{ 
                    background: 'rgba(245, 158, 11, 0.1)', 
                    padding: 16, 
                    borderRadius: 8,
                    borderLeft: '4px solid #f59e0b',
                    lineHeight: 1.6
                  }}>
                    {data.professionalAdvice.riskConsiderations}
                  </CardValue>
                </div>
              )}
              
              {/* Long-term Perspective */}
              {data.professionalAdvice.longTermPerspective && (
                <div style={{ marginBottom: 20 }}>
                  <CardLabel style={{ marginBottom: 8 }}>Long-term Perspective</CardLabel>
                  <CardValue style={{ 
                    background: 'rgba(59, 130, 246, 0.1)', 
                    padding: 16, 
                    borderRadius: 8,
                    borderLeft: '4px solid #3b82f6',
                    lineHeight: 1.6
                  }}>
                    {data.professionalAdvice.longTermPerspective}
                  </CardValue>
                </div>
              )}
              
              {/* Alternative Approaches */}
              {data.professionalAdvice.alternativeApproaches?.length > 0 && (
                <div>
                  <CardLabel style={{ marginBottom: 8 }}>Alternative Approaches</CardLabel>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {data.professionalAdvice.alternativeApproaches.map((alt, idx) => (
                      <div key={idx} style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10,
                        padding: '10px 14px',
                        background: 'rgba(139, 92, 246, 0.08)',
                        borderRadius: 8,
                        border: '1px solid rgba(139, 92, 246, 0.2)'
                      }}>
                        <span style={{ color: '#a78bfa', fontWeight: 600 }}>→</span>
                        <span style={{ fontSize: 13, color: '#e8eaed', lineHeight: 1.5 }}>{alt}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </SectionContent>
          </Section>
        )}

        {/* Client Communication */}
        {displayPrefs.clientCommunication && clientCommunication && (
          <Section>
            <SectionHeader>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
              </svg>
              <h4>Client Communication</h4>
            </SectionHeader>
            <SectionContent>
              {clientCommunication.suggestedFollowUp && (
                <CommCard>
                  <h6>Suggested Follow-up</h6>
                  <p>{clientCommunication.suggestedFollowUp}</p>
                </CommCard>
              )}
              {clientCommunication.documentsToRequest?.length > 0 && (
                <CommCard>
                  <h6>Documents to Request</h6>
                  <ul>
                    {clientCommunication.documentsToRequest.map((doc, i) => <li key={i}>{doc}</li>)}
                  </ul>
                </CommCard>
              )}
              {clientCommunication.questionsToAsk?.length > 0 && (
                <CommCard>
                  <h6>Questions to Ask</h6>
                  <ul>
                    {clientCommunication.questionsToAsk.map((q, i) => <li key={i}>{q}</li>)}
                  </ul>
                </CommCard>
              )}
            </SectionContent>
          </Section>
        )}

        {/* Long-term Outlook */}
        {displayPrefs.longTermOutlook && longTermOutlook && (
          <Section>
            <SectionHeader>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              <h4>Long-term Outlook</h4>
            </SectionHeader>
            <SectionContent>
              <CardValue>{longTermOutlook}</CardValue>
            </SectionContent>
          </Section>
        )}

        {/* What the lead (or their employer) actually completed in the widget.
            Self-fetching and renders nothing when there are none. */}
        {offersAssessments && displayPrefs.widgetAssessments && <WidgetAssessments threadId={threadId} />}
      </MainContent>

      {/* Share Modal */}
      {showShareModal && (
        <ModalOverlay onClick={() => setShowShareModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <h3>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
                Share Case Report
              </h3>
              <CloseButton onClick={() => setShowShareModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </CloseButton>
            </ModalHeader>
            
            <ModalBody>
              <SectionSelector>
                <SelectAllRow>
                  <h4>Select Sections to Include</h4>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => selectAllSections(true)}>Select All</button>
                    <button onClick={() => selectAllSections(false)}>Deselect All</button>
                  </div>
                </SelectAllRow>
                
                <CheckboxGrid>
                  <CheckboxItem className={!summary ? 'disabled' : ''}>
                    <input 
                      type="checkbox" 
                      checked={shareSections.clientSummary} 
                      onChange={() => toggleShareSection('clientSummary')}
                      disabled={!summary}
                    />
                    <span>Client Summary</span>
                  </CheckboxItem>
                  
                  <CheckboxItem className={!assessment ? 'disabled' : ''}>
                    <input 
                      type="checkbox" 
                      checked={shareSections.assessment} 
                      onChange={() => toggleShareSection('assessment')}
                      disabled={!assessment}
                    />
                    <span>Assessment & Scores</span>
                  </CheckboxItem>
                  
                  <CheckboxItem className={!blockers?.length ? 'disabled' : ''}>
                    <input 
                      type="checkbox" 
                      checked={shareSections.blockers} 
                      onChange={() => toggleShareSection('blockers')}
                      disabled={!blockers?.length}
                    />
                    <span>Blockers ({blockers?.length || 0})</span>
                  </CheckboxItem>
                  
                  <CheckboxItem className={!risks?.length ? 'disabled' : ''}>
                    <input 
                      type="checkbox" 
                      checked={shareSections.risks} 
                      onChange={() => toggleShareSection('risks')}
                      disabled={!risks?.length}
                    />
                    <span>Risks ({risks?.length || 0})</span>
                  </CheckboxItem>
                  
                  <CheckboxItem className={!pathways?.length ? 'disabled' : ''}>
                    <input 
                      type="checkbox" 
                      checked={shareSections.pathways} 
                      onChange={() => toggleShareSection('pathways')}
                      disabled={!pathways?.length}
                    />
                    <span>Pathways ({pathways?.length || 0})</span>
                  </CheckboxItem>
                  
                  <CheckboxItem className={!missingInformation?.length ? 'disabled' : ''}>
                    <input 
                      type="checkbox" 
                      checked={shareSections.missingInfo} 
                      onChange={() => toggleShareSection('missingInfo')}
                      disabled={!missingInformation?.length}
                    />
                    <span>Missing Info ({missingInformation?.length || 0})</span>
                  </CheckboxItem>
                  
                  <CheckboxItem className={!nextSteps?.length ? 'disabled' : ''}>
                    <input 
                      type="checkbox" 
                      checked={shareSections.nextSteps} 
                      onChange={() => toggleShareSection('nextSteps')}
                      disabled={!nextSteps?.length}
                    />
                    <span>Next Steps ({nextSteps?.length || 0})</span>
                  </CheckboxItem>
                  
                  <CheckboxItem className={!actionItems?.length ? 'disabled' : ''}>
                    <input 
                      type="checkbox" 
                      checked={shareSections.actionItems} 
                      onChange={() => toggleShareSection('actionItems')}
                      disabled={!actionItems?.length}
                    />
                    <span>Action Items ({actionItems?.length || 0})</span>
                  </CheckboxItem>
                  
                  <CheckboxItem className={!data?.professionalAdvice ? 'disabled' : ''}>
                    <input 
                      type="checkbox" 
                      checked={shareSections.professionalAdvice} 
                      onChange={() => toggleShareSection('professionalAdvice')}
                      disabled={!data?.professionalAdvice}
                    />
                    <span>Professional Advice</span>
                  </CheckboxItem>
                  
                  <CheckboxItem className={!clientCommunication ? 'disabled' : ''}>
                    <input 
                      type="checkbox" 
                      checked={shareSections.clientCommunication} 
                      onChange={() => toggleShareSection('clientCommunication')}
                      disabled={!clientCommunication}
                    />
                    <span>Client Communication</span>
                  </CheckboxItem>
                  
                  <CheckboxItem className={!longTermOutlook ? 'disabled' : ''}>
                    <input 
                      type="checkbox" 
                      checked={shareSections.longTermOutlook} 
                      onChange={() => toggleShareSection('longTermOutlook')}
                      disabled={!longTermOutlook}
                    />
                    <span>Long-term Outlook</span>
                  </CheckboxItem>
                  
                  <CheckboxItem className={!firmStandardsCompliance?.sections?.length ? 'disabled' : ''}>
                    <input 
                      type="checkbox" 
                      checked={shareSections.firmCompliance} 
                      onChange={() => toggleShareSection('firmCompliance')}
                      disabled={!firmStandardsCompliance?.sections?.length}
                    />
                    <span>Firm Compliance</span>
                  </CheckboxItem>
                  
                  <CheckboxItem className={!internalNotes?.length ? 'disabled' : ''}>
                    <input 
                      type="checkbox" 
                      checked={shareSections.internalNotes} 
                      onChange={() => toggleShareSection('internalNotes')}
                      disabled={!internalNotes?.length}
                    />
                    <span>Internal Notes 🔒</span>
                  </CheckboxItem>
                </CheckboxGrid>
              </SectionSelector>
              
              <SectionSelector>
                <h4>Share Options</h4>
                <ShareOptionsGrid>
                  <ShareOptionButton onClick={shareViaEmail}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span>Send via Email</span>
                    <small>Opens email client</small>
                  </ShareOptionButton>
                  
                  <ShareOptionButton onClick={copyToClipboard}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                    </svg>
                    <span>Copy to Clipboard</span>
                    <small>Plain text format</small>
                  </ShareOptionButton>
                  
                  <ShareOptionButton onClick={downloadAsHTML}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                      <polyline points="7,10 12,15 17,10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    <span>Download HTML</span>
                    <small>Styled report file</small>
                  </ShareOptionButton>
                  
                  <ShareOptionButton onClick={printReport}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="6,9 6,2 18,2 18,9" />
                      <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" />
                      <rect x="6" y="14" width="12" height="8" />
                    </svg>
                    <span>Print / Save PDF</span>
                    <small>Use browser print</small>
                  </ShareOptionButton>
                  
                  {navigator.share && (
                    <ShareOptionButton onClick={nativeShare} style={{ gridColumn: 'span 2' }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="18" cy="5" r="3" />
                        <circle cx="6" cy="12" r="3" />
                        <circle cx="18" cy="19" r="3" />
                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                      </svg>
                      <span>System Share</span>
                      <small>Use native share options</small>
                    </ShareOptionButton>
                  )}
                </ShareOptionsGrid>
              </SectionSelector>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Ask the client (from Missing Information) */}
      {askClient && (
        <InfoRequestComposer threadId={threadId} gaps={askClient} source="case_missing_info"
          defaultEmail={profileData.email || ''} defaultName={profileData.fullName || summary?.clientName || ''}
          onClose={() => setAskClient(null)} onSent={() => setFollowUpsKey(k => k + 1)} />
      )}

      {/* Resume Builder (Make Resume) */}
      {showResumeBuilder && (
        <ResumeBuilder threadId={threadId} clientName={summary?.clientName} onClose={() => setShowResumeBuilder(false)} />
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <ModalOverlay onClick={() => setShowSettingsModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <ModalHeader>
              <h3>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                Display Settings
              </h3>
              <CloseButton onClick={() => setShowSettingsModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </CloseButton>
            </ModalHeader>
            
            <ModalBody>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', marginBottom: 20 }}>
                Choose which sections to display on your Case Dashboard. Your preferences are saved automatically.
              </p>
              
              <SettingsCategory>
                <h5>Overview Sections</h5>
                <SettingsRow>
                  <div className="label">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span>Client Summary</span>
                  </div>
                  <ToggleSwitch>
                    <input type="checkbox" checked={displayPrefs.clientSummary} onChange={(e) => updateDisplayPref('clientSummary', e.target.checked)} />
                    <span></span>
                  </ToggleSwitch>
                </SettingsRow>
                
                <SettingsRow>
                  <div className="label">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <span>Quick Stats (Blockers, Risks, Readiness)</span>
                  </div>
                  <ToggleSwitch>
                    <input type="checkbox" checked={displayPrefs.quickStats} onChange={(e) => updateDisplayPref('quickStats', e.target.checked)} />
                    <span></span>
                  </ToggleSwitch>
                </SettingsRow>
              </SettingsCategory>
              
              <SettingsCategory>
                <h5>Analysis Sections</h5>
                <SettingsRow>
                  <div className="label">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <span>Firm Standards Compliance</span>
                  </div>
                  <ToggleSwitch>
                    <input type="checkbox" checked={displayPrefs.firmCompliance} onChange={(e) => updateDisplayPref('firmCompliance', e.target.checked)} />
                    <span></span>
                  </ToggleSwitch>
                </SettingsRow>
                
                <SettingsRow>
                  <div className="label">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                    <span>Immigration Pathways</span>
                  </div>
                  <ToggleSwitch>
                    <input type="checkbox" checked={displayPrefs.pathways} onChange={(e) => updateDisplayPref('pathways', e.target.checked)} />
                    <span></span>
                  </ToggleSwitch>
                </SettingsRow>

                {/* Every remaining gated section, rendered from one list.
                    These were gated in code but had no toggle here, so an adviser
                    could not switch off the longest parts of the report — which is
                    most of its length. Driving the rows from a list means a new
                    section cannot be gated without also being switchable. */}
                {visibleToggleableSections.map(({ key, label }) => (
                  <SettingsRow key={key}>
                    <div className="label">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 6h16M4 12h16M4 18h7" />
                      </svg>
                      <span>{label}</span>
                    </div>
                    <ToggleSwitch>
                      <input
                        type="checkbox"
                        checked={displayPrefs[key] !== false}
                        onChange={(e) => updateDisplayPref(key, e.target.checked)}
                      />
                      <span></span>
                    </ToggleSwitch>
                  </SettingsRow>
                ))}
                
                <SettingsRow>
                  <div className="label">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 14l9-5-9-5-9 5 9 5z" />
                      <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                    </svg>
                    <span>Professional Advice</span>
                  </div>
                  <ToggleSwitch>
                    <input type="checkbox" checked={displayPrefs.professionalAdvice} onChange={(e) => updateDisplayPref('professionalAdvice', e.target.checked)} />
                    <span></span>
                  </ToggleSwitch>
                </SettingsRow>
              </SettingsCategory>
              
              <SettingsCategory>
                <h5>Action Sections</h5>
                <SettingsRow>
                  <div className="label">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Action Items</span>
                  </div>
                  <ToggleSwitch>
                    <input type="checkbox" checked={displayPrefs.actionItems} onChange={(e) => updateDisplayPref('actionItems', e.target.checked)} />
                    <span></span>
                  </ToggleSwitch>
                </SettingsRow>
                
                <SettingsRow>
                  <div className="label">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
                      <rect x="9" y="3" width="6" height="4" rx="2" />
                    </svg>
                    <span>Missing Information</span>
                  </div>
                  <ToggleSwitch>
                    <input type="checkbox" checked={displayPrefs.missingInfo} onChange={(e) => updateDisplayPref('missingInfo', e.target.checked)} />
                    <span></span>
                  </ToggleSwitch>
                </SettingsRow>
                
                <SettingsRow>
                  <div className="label">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 5l7 7-7 7" />
                    </svg>
                    <span>Next Steps</span>
                  </div>
                  <ToggleSwitch>
                    <input type="checkbox" checked={displayPrefs.nextSteps} onChange={(e) => updateDisplayPref('nextSteps', e.target.checked)} />
                    <span></span>
                  </ToggleSwitch>
                </SettingsRow>
              </SettingsCategory>
              
              <SettingsCategory>
                <h5>Communication & Notes</h5>
                <SettingsRow>
                  <div className="label">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14.5 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V7.5L14.5 2z" />
                      <polyline points="14,2 14,8 20,8" />
                    </svg>
                    <span>Internal Notes</span>
                  </div>
                  <ToggleSwitch>
                    <input type="checkbox" checked={displayPrefs.internalNotes} onChange={(e) => updateDisplayPref('internalNotes', e.target.checked)} />
                    <span></span>
                  </ToggleSwitch>
                </SettingsRow>
                
                <SettingsRow>
                  <div className="label">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                    </svg>
                    <span>Client Communication</span>
                  </div>
                  <ToggleSwitch>
                    <input type="checkbox" checked={displayPrefs.clientCommunication} onChange={(e) => updateDisplayPref('clientCommunication', e.target.checked)} />
                    <span></span>
                  </ToggleSwitch>
                </SettingsRow>
                
                <SettingsRow>
                  <div className="label">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    <span>Long-term Outlook</span>
                  </div>
                  <ToggleSwitch>
                    <input type="checkbox" checked={displayPrefs.longTermOutlook} onChange={(e) => updateDisplayPref('longTermOutlook', e.target.checked)} />
                    <span></span>
                  </ToggleSwitch>
                </SettingsRow>
              </SettingsCategory>
              
              <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid rgba(99, 102, 241, 0.2)' }}>
                <button 
                  onClick={resetDisplayPrefs}
                  style={{
                    background: 'transparent',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    color: '#f87171',
                    padding: '8px 16px',
                    borderRadius: 6,
                    fontSize: 13,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Reset to Defaults
                </button>
              </div>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Copied Toast */}
      {showCopiedToast && (
        <CopiedToast>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 13l4 4L19 7" />
          </svg>
          Copied to clipboard!
        </CopiedToast>
      )}
    </Container>
  );
};

export default CaseDashboard;
